import _extends from "@babel/runtime/helpers/extends";
import { handleActions, combineActions } from 'redux-actions';
import is from '@sindresorhus/is';
import onlyRunInDev from '../../../utils/onlyRunInDev'; // Type checking for the action map

var validateActionMap = onlyRunInDev(function (_ref) {
  var loading = _ref.loading,
      loaded = _ref.loaded,
      failed = _ref.failed;
  var message = 'createRequestReducer was called without a valid actionMap key. ' + 'Please ensure you have a `loading`, `loaded, and `failed` key set ' + 'under `actionMap`. Each of these must be arrays with at least 1 action';

  if (!loading || !loaded || !failed) {
    throw new Error(message);
  }

  if (!is.array(loading) || !is.array(loaded) || !is.array(failed)) {
    throw new Error(message);
  }

  if (!loading.length || !loaded.length || !failed.length) {
    throw new Error(message);
  }
});

var loading = function loading(state) {
  return _extends({}, state, {
    loading: true,
    failed: false,
    error: null
  });
};

var loaded = function loaded(state, action) {
  return _extends({}, state, {
    loading: false,
    loaded: true,
    count: action.payload.count,
    next: action.payload.next
  });
};

var failed = function failed(state, action) {
  return _extends({}, state, {
    loading: false,
    failed: true,
    error: action.payload
  });
};
/**
 * Creates a new request reducer, which is used to track the lifecycle of an http request.
 *
 * The request reducer has the following initial state:
 *
 * ```js
 * {
 *   loading: false, // indicates a request is in progress
 *   loaded: false, // indicates that data has been successfully loaded
 *   failed: false, // indicates that a request failed
 *   error: null, // The error object if a request failed
 *   next: null, // The next url (for pagination)
 *   count: null, // The total count of all objects on the backend
 * }
 * ```
 *
 * It supports three types of updates. If a value in state is not changed by the update, it will stay the same as the previous state:
 *
 * - `loading`: Sets `loading` to true, `failed` to false, and `error` to null. Purposefully does not set `loaded` to false so that initial requests can be tracked more easily (!loaded && !loading)
 * - `loaded`: Sets `loading` to false, `loaded` to true, `count` to `action.payload.count`, and `next` to `action.payload.next`
 * - `failed`: Sets `loading` to false, `failed` to `true`, and `error` to `action.payload`
 *
 * ```js
 * // Example Implementation
 *
 * import { createRequestReducer } from '@minerva-analytics/pantheon';
 * import * as actions from '../../actions/confirmSubscriber';
 *
 * const byId = createRequestReducer({
 *   actionMap: {
 *     loading: [actions.confirmSubscriberStart]
 *     loaded: [actions.confirmSubscriberSuccess]
 *     failed: [actions.confirmSubscriberFailure]
 *   },
 * })
 * ```
 * Currently, there are no supported options for the request reducer.
 *
 * @param {Object} config - The configuration for the request reducer
 * @param {Object} config.actionMap - The mapping of actions to updates
 * @param {Array} config.actionMap.loading - The array of actions that should trigger a `loading` update
 * @param {Array} config.actionMap.loaded - The array of actions that should trigger a `loaded` update
 * @param {Array} config.actionMap.failed - The array of actions that should trigger a `failed` update
 *
 * @returns {Function} - A newly created request reducer
 */


var createRequestReducer = function createRequestReducer(_ref2) {
  var _ref2$actionMap = _ref2.actionMap,
      actionMap = _ref2$actionMap === void 0 ? {} : _ref2$actionMap;
  validateActionMap(actionMap);
  var initialState = {
    loading: false,
    loaded: false,
    failed: false,
    error: null,
    next: null,
    count: null
  };
  return handleActions(new Map([[combineActions.apply(void 0, actionMap.loading), loading], [combineActions.apply(void 0, actionMap.loaded), loaded], [combineActions.apply(void 0, actionMap.failed), failed]]), initialState);
};

export default createRequestReducer;